import { v4 } from 'uuid';
import { isComponentDataV1, transformComponentDataV1ToV2, isComponentEntryV1, transformComponentEntryV1ToV2 } from '@i-ark/api';
import { getDisplayData, opgavekompleksetDataSchema, opgavekompleksetEntryDataSchema, postProcessOpgavekompleksetData, postProcessOpgavekompleksetEntry, imageHotspotsDataSchema, upgradeImageHotspotsV1Data, iArkDataSchema, iArkEntryDataSchema, geogebraDataSchema, geogebraEntrySchema, flashcardDataSchema, flashcardEntrySchema, iGuideDataSchema, iGuideEntrySchema, getComponentDataQuery, tanstackQueryClient, fetchEntriesForComponentQuery, makeComponentEntryEnvelopeFromComponentEnvelope, getComponentEntryQueryKey, getEntriesForComponentQueryKey } from '@utils/component-framework';

function resolveComponentFrameworkNodes(queryClient, ast, user) {
  return Promise.all(
    ast.map(async (node) => {
      var _a;
      if (node.nodeType !== "iframe-component.component-framework") {
        if (node.nodeType === "text-component") {
          return node;
        }
        if (!("children" in node) || !node.children) {
          return node;
        }
        return {
          ...node,
          children: await resolveComponentFrameworkNodes(
            queryClient,
            node.children,
            user
          )
        };
      }
      const displayMode = (() => {
        if (!node.options.autoActivate) {
          if (node.options.desiredDisplayMode === "embed") {
            return node.options.monorepo__openAsLightbox ? "lightbox" : "harmonica";
          }
          return node.options.desiredDisplayMode ?? "harmonica";
        }
        return node.options.desiredDisplayMode ?? "embed";
      })();
      try {
        const displayData = await getDisplayData(node.referenceId);
        const displayConfig = (() => {
          switch (displayData.ComponentType) {
            case "i-guide": {
              return {
                componentType: "i-guide",
                componentDataSchema: iGuideDataSchema,
                componentEntrySchema: iGuideEntrySchema
              };
            }
            case "flashcard": {
              return {
                componentType: "flashcard",
                componentDataSchema: flashcardDataSchema,
                componentEntrySchema: flashcardEntrySchema
              };
            }
            case "geogebra": {
              return {
                componentType: "geogebra",
                componentDataSchema: geogebraDataSchema,
                componentEntrySchema: geogebraEntrySchema
              };
            }
            case "arbejdsark": {
              return {
                componentType: "i-ark",
                componentDataSchema: iArkDataSchema,
                componentEntrySchema: iArkEntryDataSchema,
                transformDataPre: async (data) => isComponentDataV1(data) ? await transformComponentDataV1ToV2(data) : data,
                transformEntryPost: async (entry, data) => isComponentEntryV1(entry) ? transformComponentEntryV1ToV2(data, entry) : entry
              };
            }
            case "image-hotspot": {
              return {
                componentType: "image-hotspots",
                componentDataSchema: imageHotspotsDataSchema,
                componentEntrySchema: void 0,
                transformDataPost: async (data) => data.version === "v1" ? upgradeImageHotspotsV1Data(data) : data
              };
            }
            case "opgavekompleks-flex":
            case "opgavekompleks-simpel":
              return {
                componentType: "opgavekomplekset",
                componentDataSchema: opgavekompleksetDataSchema,
                componentEntrySchema: opgavekompleksetEntryDataSchema,
                transformDataPost: postProcessOpgavekompleksetData,
                transformEntryPost: postProcessOpgavekompleksetEntry
              };
          }
        })();
        if (!displayConfig) {
          return node;
        }
        const componentDataQuery = getComponentDataQuery(
          displayData.ComponentID,
          {
            schema: displayConfig.componentDataSchema,
            processors: {
              preValidation: displayConfig.transformDataPre,
              // @ts-expect-error Our generics does not handle transform of
              // unions of schemas well, but this does not matter too much at
              // this stage as we're working with our data at an abstract level.
              postValidation: displayConfig.transformDataPost
            }
          }
        );
        const componentDataPromise = tanstackQueryClient.fetchQuery(componentDataQuery);
        const entriesForComponentQuery = displayConfig.componentEntrySchema && (user == null ? void 0 : user.id) ? fetchEntriesForComponentQuery(user.id, displayData.ComponentID, {
          schema: displayConfig.componentEntrySchema,
          processors: {
            postValidation: displayConfig.transformEntryPost ? (entry) => (
              // @ts-expect-error See comment above
              componentDataPromise.then(
                (component) => {
                  var _a2;
                  return (_a2 = displayConfig.transformEntryPost) == null ? void 0 : _a2.call(
                    displayConfig,
                    // @ts-expect-error See comment above
                    entry,
                    component
                  );
                }
              )
            ) : void 0
          }
        }) : void 0;
        const [componentData, componentEntries] = await Promise.all([
          componentDataPromise,
          user && entriesForComponentQuery ? tanstackQueryClient.fetchQuery(entriesForComponentQuery) : void 0
        ]);
        const latestComponentEntry = componentEntries == null ? void 0 : componentEntries.at(-1);
        if (latestComponentEntry && latestComponentEntry.state === "invalid") {
          tanstackQueryClient.removeQueries({
            queryKey: entriesForComponentQuery == null ? void 0 : entriesForComponentQuery.queryKey,
            exact: true
          });
        }
        if (componentData.state !== "valid") {
          tanstackQueryClient.removeQueries({
            queryKey: componentDataQuery.queryKey,
            exact: true
          });
          throw new Error(
            `resolveComponentFrameworkNodes(): invalid component state${componentData.state === "invalid" ? `

${componentData.error}` : ""}`
          );
        }
        if (latestComponentEntry && latestComponentEntry.state === "invalid") {
          console.error(
            "resolveComponentFrameworkNodes(): received invalid component entry data, ignoring the entry",
            latestComponentEntry
          );
        }
        const entryKey = (latestComponentEntry == null ? void 0 : latestComponentEntry.state) === "valid" ? latestComponentEntry.envelope.objectID ?? v4() : v4();
        const learningObject = node.pm2.learningObjectId && node.pm2.learningObjectTitle ? {
          id: node.pm2.learningObjectId,
          title: "title" in componentData.data && typeof componentData.data.title === "string" ? componentData.data.title : (latestComponentEntry == null ? void 0 : latestComponentEntry.state) === "valid" ? latestComponentEntry.envelope.title : node.pm2.learningObjectTitle,
          type: "component"
        } : void 0;
        const entryData = (latestComponentEntry == null ? void 0 : latestComponentEntry.state) === "valid" ? latestComponentEntry.data : ((_a = displayConfig.componentEntrySchema) == null ? void 0 : _a.parse({})) ?? {};
        const entryEnvelope = (latestComponentEntry == null ? void 0 : latestComponentEntry.state) === "valid" ? latestComponentEntry.envelope : makeComponentEntryEnvelopeFromComponentEnvelope(
          user,
          componentData.envelope
        );
        queryClient.setQueryData(
          getComponentEntryQueryKey(entryKey),
          {
            state: {
              result: "success",
              isMutating: false
            },
            entryKey,
            data: entryData,
            envelope: entryEnvelope,
            learningObject
          }
        );
        queryClient.setQueryData(
          getEntriesForComponentQueryKey(componentData.envelope.ID),
          (prev) => prev ?? (componentEntries == null ? void 0 : componentEntries.map(
            (entry) => entry.state === "valid" ? {
              data: entry.data,
              envelope: entry.envelope
            } : void 0
          ).filter((it) => !!it))
        );
        return {
          nodeType: `component-framework.${displayConfig.componentType}`,
          id: node.pm2.learningObjectId,
          pm2: {
            ...node.pm2,
            learningObjectId: learningObject == null ? void 0 : learningObject.id,
            learningObjectTitle: learningObject == null ? void 0 : learningObject.title
          },
          meta: node.meta,
          options: {
            displayMode,
            ...node.options
          },
          component: {
            entryKey,
            envelope: componentData.envelope,
            data: componentData.data
          }
        };
      } catch {
        return {
          nodeType: "component-framework.error",
          id: node.pm2.learningObjectId,
          pm2: node.pm2,
          meta: node.meta,
          options: {
            displayMode,
            ...node.options
          }
        };
      }
    })
  );
}

export { resolveComponentFrameworkNodes };
//# sourceMappingURL=util.resolve-component-framework-nodes.js.map
